/*
Jira: https://mch-applicationservices.atlassian.net/browse/AB-702
Figma: https://www.figma.com/file/4W9eSTn0I9bZuE6mZ4pT4C/branch/tgRCzOzQNanNeN7Jr4DZUW/Article-Layout?node-id=12631%3A296039&t=3doX06MVVORJlBrg-0
Storybook: https://storybook-uikit-dev.azurewebsites.net/?path=/story/components-structure-media-media-caption--media-caption
*/
import React, { FC, useContext } from 'react'
import { MediaCaption, Container, Row, Col } from '@mch-group/uikit-components'
import { Media, MediaContextProvider } from '@responsive'
import { uploadTransformationUrl } from '@dmi-mch/utils'
import { cloudinary } from '@constants'
import { detectDpr, detectAspectRatio } from '@utils'
import styled from 'styled-components'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import { PageContext } from '@providers/PageContext'
import classNames from 'classnames'
import isTargetingCookiesTypeAccepted from '@utils/isTargetingCookiesAccepted'
import acceptTargetingCookies from '@utils/acceptTargetingCookies'

interface IEMSingleMedia {
  id: string | undefined
  moduleName: string
  emsm_image_upload: string
  emsm_media_caption: string
  emsm_media_width: string
  emsm_video_upload: string
  emsm_media_priority: string
  emsm_youtube_video: string
}

const EMSingleMedia: FC<IEMSingleMedia> = ({
  id,
  emsm_image_upload,
  emsm_media_caption,
  emsm_media_width = 'large',
  emsm_video_upload,
  emsm_media_priority = 'image',
  emsm_youtube_video
}) => {
  const imageSrc = emsm_image_upload && emsm_image_upload?.split('?')[0]
  let emsmMediaPriority = emsm_media_priority

  if (emsm_media_priority === 'youtube_video' && emsm_youtube_video) {
    emsmMediaPriority = 'video'
  }

  let urlVideo = ''

  let videoCoverImage: string | null = imageSrc || null
  if (emsm_media_priority === 'video' || emsm_media_priority === 'snippet') {
    urlVideo = emsm_video_upload
      ? `https://res.cloudinary.com/${getEnvConfig.fe.cloudinary.name}/video/upload/sp_auto/${emsm_video_upload}.m3u8`
      : ''
  } else if (emsm_media_priority === 'youtube_video') {
    urlVideo = emsm_youtube_video
    videoCoverImage = videoCoverImage ? videoCoverImage : ''
  }
  const { page: { siteLanguage } } = useContext(PageContext)

  const labels = useLabelsFromAPI(['EditorialModules', 'YoutubeCookieModal'], siteLanguage)

  const props = {
    fullWidth: emsm_media_width === 'full bleed',
    imageAltText: emsm_media_caption,
    urlVideo,
    media: emsmMediaPriority as IMediaCaption['media'],
    caption: emsm_media_caption !== null,
    captionText: emsm_media_caption,
    videoCoverImage: videoCoverImage as IMediaCaption['videoCoverImage'],
    ctaLabel: labels?.labels?.EditorialModules?.videoCTAText || 'Watch Now'
  }

  const ar = () => {
    if (emsm_media_priority == 'image' && emsm_media_width !== 'full bleed') {
      return emsm_image_upload ? detectAspectRatio(emsm_image_upload, 'qs', 'inline') : null
    }
    return null
  }

  const getImage = (w) =>
    uploadTransformationUrl(imageSrc, `q_auto,dpr_${detectDpr('dpr_')},c_fill,f_auto,w_${w}`, {
      transformUrl: cloudinary.throughCloudfront
    })
  const noCookiesModalProp: { noCookiesModal: IMediaCaption['noCookiesModal'] } = {
    noCookiesModal: emsm_media_priority === 'youtube_video' ? {
      heading: labels?.labels?.YoutubeCookieModal?.modalTitle || '',
      description: labels?.labels?.YoutubeCookieModal?.modalDescription || '',
      ctaLabel: labels?.labels?.YoutubeCookieModal?.acceptAndWatchBtnLabel || '',
      isModalNeeded: (setCookiesAccepted) => {
        setCookiesAccepted(isTargetingCookiesTypeAccepted())
      },
      onClickAcceptCookies: () => {
        acceptTargetingCookies()
      }
    } : undefined
  }

  return (
    <section
      className={classNames('em-single-media', siteLanguage === 'zh_CN' ?
        'mb-7 mb-md-8' : 'mb-9 mb-md-12')}
      id={id}
      data-property={`${emsm_media_width ?? emsm_media_width} ${emsm_media_priority ?? emsm_media_priority}`}
    >
      {/*  Small variant */}
      {emsm_media_width === 'small' && (
        <Container className='px-5 px-lg-8 px-md-7 mt-0'>
          <Row gutter='g-5 g-md-6 g-xl-7' className='my-0 py-0'>
            <Col
              xs={{ span: 12, offset: 0 }}
              sm={{ span: 12, offset: 0 }}
              md={{ span: 8, offset: 2 }}
              lg={{ span: 8, offset: 2 }}
              xl={{ span: 6, offset: 3 }}
              className='my-0 py-0'
            >
              <StyledMediaCaption ar={ar()}>
                <Media at='xs'>
                  <StyledMediaCaption ar={ar()}>
                    <MediaCaption {...props} urlImage={getImage(365)} className='mb-0' {...noCookiesModalProp} />
                  </StyledMediaCaption>
                </Media>
                <Media at='sm'>
                  <StyledMediaCaption ar={ar()}>
                    <MediaCaption {...props} urlImage={getImage(724)} className='mb-0' {...noCookiesModalProp} />
                  </StyledMediaCaption>
                </Media>
                <Media greaterThanOrEqual='tablet'>
                  <MediaCaption {...props} urlImage={getImage(724)} className='mb-0' {...noCookiesModalProp} />
                </Media>
              </StyledMediaCaption>
            </Col>
          </Row>
        </Container>
      )}

      {/*  Medium variant */}
      {emsm_media_width === 'medium' && (
        <MediaContextProvider>
          <Media at='xs'>
            <StyledMediaCaption ar={ar()}>
              <MediaCaption {...{ ...props, fullWidth: true }} urlImage={getImage(397)} className='mb-0' {...noCookiesModalProp} />
            </StyledMediaCaption>
          </Media>
          <Media at='sm'>
            <StyledMediaCaption ar={ar()}>
              <MediaCaption {...{ ...props, fullWidth: true }} urlImage={getImage(752)} className='mb-0' {...noCookiesModalProp} />
            </StyledMediaCaption>
          </Media>
          <Media greaterThanOrEqual='tablet'>
            <Container className='px-5 px-lg-8 px-md-7 mt-0'>
              <Row gutter='g-5 g-md-6 g-xl-7 mt-0'>
                <Col
                  className='my-0 py-0'
                  md={{ span: 10, offset: 1 }}
                  lg={{ span: 8, offset: 2 }}
                  xl={{ span: 10, offset: 1 }}
                >
                  <StyledMediaCaption ar={ar()}>
                    <MediaCaption {...props} urlImage={getImage(1223)} className='mb-0' {...noCookiesModalProp} />
                  </StyledMediaCaption>
                </Col>
              </Row>
            </Container>
          </Media>
        </MediaContextProvider>
      )}

      {/*  Large variant */}
      {emsm_media_width === 'large' && (
        <MediaContextProvider>
          <Media at='xs'>
            <StyledMediaCaption ar={ar()}>
              <MediaCaption {...{ ...props, fullWidth: true }} urlImage={getImage(397)} className='mb-0' {...noCookiesModalProp} />
            </StyledMediaCaption>
          </Media>
          <Media at='sm'>
            <StyledMediaCaption ar={ar()}>
              <MediaCaption {...{ ...props, fullWidth: true }} urlImage={getImage(752)} className='mb-0' {...noCookiesModalProp} />
            </StyledMediaCaption>
          </Media>
          <Media greaterThanOrEqual='tablet'>
            <Container className='px-5 px-lg-8 px-md-7 mt-0'>
              <StyledMediaCaption ar={ar()}>
                <MediaCaption {...props} urlImage={getImage(1472)} className='mb-0' {...noCookiesModalProp} />
              </StyledMediaCaption>
            </Container>
          </Media>
        </MediaContextProvider>
      )}

      {/*  Full Width */}
      {props.fullWidth && (
        <MediaContextProvider>
          <Media lessThan='tablet'>
            <StyledMediaCaption>
              <MediaCaption {...props} urlImage={getImage(752)} className='mb-0' {...noCookiesModalProp} />
            </StyledMediaCaption>
          </Media>
          <Media greaterThanOrEqual='tablet'>
            <StyledMediaCaption>
              <MediaCaption {...props} urlImage={getImage(1905)} className='mb-0' {...noCookiesModalProp} />
            </StyledMediaCaption>
          </Media>
        </MediaContextProvider>
      )}
    </section>
  )
}

interface Props {
  ar: string | null
}

const StyledMediaCaption = styled.div<any>`
  figure,
  img {
    aspect-ratio: ${(props: Props) => (props.ar ? props.ar : 'inherit')};
  }

  img {
    display: flex;
  }
`

// Some TS magic to obtain the typings of the EditorialHero component
type IMediaCaption = React.ComponentProps<typeof MediaCaption>

export default EMSingleMedia
